// Ряд вложений над репликой оператора (README §2в п.5): у картинок превью
// 64×64 (radius 6, клик — на весь экран), у остальных — плашка
// «расширение · имя · размер», клик скачивает. Файл берётся с сервера с
// токеном в заголовке — только своей сессии (проверяет сервер).

import { memo, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { AttachmentMeta } from '../../protocol'
import { attachmentBlob, downloadAttachment } from '../../transport/cliApi'

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`
  const mb = bytes / 1024 / 1024
  return `${Number.isInteger(mb) || mb >= 10 ? Math.round(mb) : mb.toFixed(1).replace('.', ',')} МБ`
}

export function extOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase().slice(0, 5) : 'файл'
}

function Thumb({ sessionId, item, onOpen }: {
  sessionId: string
  item: AttachmentMeta
  onOpen(url: string): void
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    attachmentBlob(sessionId, item.id, true).then(
      (u) => { if (alive) setUrl(u) },
      () => { if (alive) setFailed(true) },
    )
    return () => { alive = false }
  }, [sessionId, item.id])
  return (
    <button
      type="button"
      className={`cli-thumb${failed ? ' is-failed' : ''}`}
      title={`${item.name} · ${humanSize(item.size)} — открыть`}
      onClick={() => url && onOpen(url)}
    >
      {url ? <img src={url} alt={item.name} /> : <span>{failed ? 'нет файла' : '…'}</span>}
    </button>
  )
}

export function FilePill({ item, onClick, children }: {
  item: { name: string; size: number; executable?: boolean }
  onClick?: () => void
  children?: React.ReactNode
}) {
  return (
    <button
      type="button"
      className={`cli-pill${item.executable ? ' is-exec' : ''}`}
      title={item.executable ? `${item.name} — исполняемый файл` : item.name}
      onClick={onClick}
    >
      <span className="cli-pill__ext">{extOf(item.name)}</span>
      <span className="cli-pill__name">{item.name}</span>
      <span className="cli-pill__size">{humanSize(item.size)}</span>
      {item.executable ? <span className="cli-pill__exec">исполняемый</span> : null}
      {children}
    </button>
  )
}

export function Lightbox({ url, name, onClose }: { url: string; name: string; onClose(): void }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', key, true)
    return () => window.removeEventListener('keydown', key, true)
  }, [onClose])
  return createPortal(
    <div className="cli-lightbox" role="dialog" aria-label={name} onClick={onClose}>
      <img src={url} alt={name} />
      <span className="cli-lightbox__name">{name} · Esc — закрыть</span>
    </div>,
    document.body,
  )
}

function AttachRow({ sessionId, items }: { sessionId: string; items: AttachmentMeta[] }) {
  const [open, setOpen] = useState<{ url: string; name: string } | null>(null)
  return (
    <div className="cli-attrow">
      {items.map((item) => (item.kind === 'image' ? (
        <Thumb key={item.id} sessionId={sessionId} item={item} onOpen={(url) => setOpen({ url, name: item.name })} />
      ) : (
        <FilePill
          key={item.id}
          item={item}
          onClick={() => { void downloadAttachment(sessionId, item.id, item.name).catch(() => {}) }}
        />
      )))}
      {open ? <Lightbox url={open.url} name={open.name} onClose={() => setOpen(null)} /> : null}
    </div>
  )
}

export default memo(AttachRow)
